import { useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { InspectionTemplateBody, LayoutBody } from '@ayumia/contracts';
import { blockComponent, resolveBinding, type AggregateValue, type FormFormat, type LayoutBlock } from '@ayumia/component-runtime';
import { text } from '../app/i18n';
import { Dialog, DialogActions } from '../design-system/Overlay';
import { AppServicesContext } from '../views/ViewContext';
import { selectorLabel, selectorOf, type SelectorRow } from './object-selector';
import type { Section } from './form-targets';
import './form-layout.css';

/**
 * Rendu web de la couche de mise en page d'un formulaire (inspection-template-body 1.8.0, ADR-146 §2) :
 * format d'affichage, régions du gabarit de mise en page, blocs hors champs (bandeau d'objet, emplacement
 * neutre), confirmation avant l'envoi, lecture seule. Les calculs (pages, manques, agrégats, liaisons)
 * sont ceux du Core, importés tels quels (`@ayumia/component-runtime`). Présentation seulement : le
 * serveur relit la fiche entière. Jetons du thème seulement.
 */

type Region = LayoutBody['regions'][number];
type Localized = Readonly<Record<string, string>>;

/** Largeur sous laquelle le web passe au format mobile (règle partagée avec la tablette). */
export const MOBILE_MAX_WIDTH = 600;

/** Format du web : `web`, ou `mobile` sous {@link MOBILE_MAX_WIDTH} px ; `forced` l'impose (aperçu, test). */
export function useFormFormat(forced?: FormFormat): FormFormat {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 1024 : window.innerWidth));
  useEffect(() => {
    if (forced !== undefined || typeof window === 'undefined') return;
    const onResize = (): void => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [forced]);
  if (forced !== undefined) return forced;
  return width < MOBILE_MAX_WIDTH ? 'mobile' : 'web';
}

/** Règle d'un format : la sienne, sinon celle du paysage (« un format absent suit le paysage, web compris »). */
function ruleOf(body: LayoutBody, format: FormFormat) {
  const formats = body.formats ?? {};
  return formats[format] ?? formats.landscape ?? {};
}

export interface ArrangedRegions {
  /** Pleine largeur, en haut : l'en-tête, puis les bandes du haut. */
  top: Region[];
  /** Colonnes (paysage, web) ou pile (portrait empilé, mobile). */
  columns: Region[];
  /** Pleine largeur, en bas : les bandes du bas, puis le pied. */
  bottom: Region[];
  stacked: boolean;
}

/**
 * Disposition des régions dans un format : l'ordre déclaré par le format (`order`), les régions absentes
 * de l'ordre après les autres ; l'en-tête et le pied en pleine largeur ; une région en bande (`bands`)
 * passe en haut ou en bas ; `stack` (et le mobile) empile les colonnes.
 */
export function arrangeRegions(body: LayoutBody, format: FormFormat): ArrangedRegions {
  const rule = ruleOf(body, format);
  const order = rule.order ?? [];
  const ranked = [...body.regions].sort((a, b) => {
    const ia = order.indexOf(a.region_id);
    const ib = order.indexOf(b.region_id);
    return (ia < 0 ? order.length + body.regions.indexOf(a) : ia) - (ib < 0 ? order.length + body.regions.indexOf(b) : ib);
  });
  const bands = (rule as { bands?: Readonly<Record<string, 'top' | 'bottom'>> }).bands ?? {};
  // La bande de champs (rôle `strip`, layout-body 1.1.0) suit l'en-tête, en pleine largeur.
  const strip = (region: Region): boolean => region.role === 'strip';
  const top = [...ranked.filter((region) => region.role === 'header'), ...ranked.filter(strip), ...ranked.filter((region) => region.role !== 'header' && region.role !== 'footer' && !strip(region) && bands[region.region_id] === 'top')];
  const bottom = [...ranked.filter((region) => region.role !== 'header' && region.role !== 'footer' && bands[region.region_id] === 'bottom'), ...ranked.filter((region) => region.role === 'footer')];
  const columns = ranked.filter((region) => !top.includes(region) && !bottom.includes(region));
  return { top, columns, bottom, stacked: format === 'mobile' || rule.stack === true };
}

/** Région d'une section : celle de son placement si le gabarit l'a, sinon la première qui admet les sections. */
export function sectionRegion(body: LayoutBody | undefined, declared: string | undefined): string | undefined {
  if (body === undefined) return undefined;
  if (declared !== undefined && body.regions.some((region) => region.region_id === declared)) return declared;
  return body.regions.find((region) => region.accepts.includes('section'))?.region_id ?? body.drop_region;
}

/** Région d'un bloc : la sienne si le gabarit l'a, sinon la région de dépôt, sinon la première principale. */
export function blockRegion(body: LayoutBody | undefined, declared: string): string | undefined {
  if (body === undefined) return undefined;
  if (body.regions.some((region) => region.region_id === declared)) return declared;
  return body.drop_region ?? body.regions.find((region) => region.role === 'main')?.region_id;
}

/**
 * Régions d'une page : chaque région non vide à sa place ; une région vide n'est pas rendue. Sans gabarit
 * de mise en page servi : une colonne, dans l'ordre reçu.
 */
export function RegionLayout({ body, format, contents, fallback, hiddenRoles = new Set() }: { body: LayoutBody | undefined; format: FormFormat; contents: ReadonlyMap<string, readonly ReactNode[]>; fallback: readonly ReactNode[]; hiddenRoles?: ReadonlySet<string> }) {
  if (body === undefined) return <div className="ay-layout ay-layout--single">{fallback}</div>;
  const arranged = arrangeRegions(body, format);
  // Une région vide ou masquée par la recette du poste (par son rôle) n'est pas rendue.
  const filled = (region: Region): boolean => !hiddenRoles.has(region.role) && (contents.get(region.region_id) ?? []).length > 0;
  const regionNode = (region: Region, style?: CSSProperties, band?: 'top' | 'bottom'): ReactNode => (
    <div key={region.region_id} className={`ay-layout__region ay-layout__region--${region.role}`} data-region={region.region_id} data-band={band} style={style}>
      {contents.get(region.region_id)}
    </div>
  );
  const columns = arranged.columns.filter(filled);
  return (
    <div className="ay-layout" data-format={format}>
      {arranged.top.filter(filled).map((region) => regionNode(region, undefined, region.role === 'header' || region.role === 'strip' ? undefined : 'top'))}
      {columns.length > 0 && (
        <div className={`ay-layout__columns${arranged.stacked ? ' ay-layout__columns--stacked' : ''}`}>
          {columns.map((region) => regionNode(region, arranged.stacked ? undefined : { flexGrow: region.width ?? 6, flexBasis: 0 }))}
        </div>
      )}
      {arranged.bottom.filter(filled).map((region) => regionNode(region, undefined, region.role === 'footer' ? undefined : 'bottom'))}
    </div>
  );
}

/** Initiales d'un libellé : deux lettres au plus. */
export function initials(label: string): string {
  return label
    .split(/[\s—-]+/)
    .filter((word) => /\p{L}|\d/u.test(word))
    .slice(0, 2)
    .map((word) => [...word][0]!.toUpperCase())
    .join('');
}

/**
 * Bandeau d'objet (`ObjectBanner`) : l'intitulé, le libellé de l'objet relié (lu comme le sélecteur
 * d'objet, sous l'autorisation de l'utilisateur), ses initiales. Rien de choisi, ou lecture refusée : « — ».
 */
export function ObjectBanner({ block, template, scope }: { block: LayoutBlock; template: InspectionTemplateBody; scope: Readonly<Record<string, unknown>> }) {
  const services = useContext(AppServicesContext);
  const path = (block.bind as Readonly<Record<string, { path: string }>> | undefined)?.['object']?.path;
  const raw = path === undefined ? undefined : resolveBinding(path, scope);
  const id = typeof raw === 'string' && raw !== '' ? raw : undefined;
  const fieldId = path !== undefined && path.startsWith('$answers.') && path.split('.').length === 2 ? path.split('.')[1] : undefined;
  const entry = fieldId === undefined ? undefined : template.sections.flatMap((section: Section) => [...section.fields]).find((candidate) => candidate.field.field_id === fieldId);
  const type = (entry?.field as { reference_type?: string } | undefined)?.reference_type;
  const [label, setLabel] = useState<{ id: string; text: string | undefined } | undefined>(undefined);
  useEffect(() => {
    if (id === undefined || type === undefined || services === null || entry === undefined) return;
    let alive = true;
    services.client.listObjects<SelectorRow>(type, { filter: { id: { in: id } }, limit: 1 }).then(
      (envelope) => {
        const row = envelope.data.find((candidate) => String(candidate.id) === id);
        if (alive) setLabel({ id, text: row === undefined ? undefined : selectorLabel(row, selectorOf(entry)) });
      },
      () => {
        // Lecture refusée ou hors ligne : le bandeau reste vide, jamais une donnée devinée.
        if (alive) setLabel({ id, text: undefined });
      },
    );
    return () => {
      alive = false;
    };
  }, [id, type, services, entry]);
  const shown = id !== undefined && label?.id === id ? label.text : undefined;
  const props = (block.props ?? {}) as Readonly<Record<string, unknown>>;
  const eyebrow = props['eyebrow'] !== null && typeof props['eyebrow'] === 'object' ? text(props['eyebrow'] as Localized) : text(block.label as Localized | undefined);
  const variant = block.variant === 'large' || block.variant === 'line' ? block.variant : 'compact';
  return (
    <div className={`ay-banner-object ay-banner-object--${variant}`} data-testid={`block-${block.block_id}`} role="group" aria-label={eyebrow === '' ? undefined : eyebrow}>
      {variant !== 'line' && (
        <span className="ay-banner-object__avatar" aria-hidden="true">
          {shown === undefined ? '—' : initials(shown) || '—'}
        </span>
      )}
      <span className="ay-banner-object__text">
        {eyebrow !== '' && <span className="ay-banner-object__eyebrow">{eyebrow}</span>}
        <strong className="ay-banner-object__label" data-testid={`block-${block.block_id}-label`}>
          {shown ?? '—'}
        </strong>
      </span>
    </div>
  );
}

/** Bloc qu'aucun rendu de ce support ne connaît : un emplacement neutre, jamais une erreur. */
export function UnsupportedBlock({ block }: { block: LayoutBlock }) {
  return (
    <div className="ay-layout__unsupported" role="note" data-testid={`block-${block.block_id}`} data-component={blockComponent(block).name}>
      Bloc non pris en charge sur cet appareil
    </div>
  );
}

/** Valeur d'un agrégat en texte : la valeur exacte, virgule française, unité. */
export function aggregateShown(value: AggregateValue): string {
  const shown = value.text === '' ? '—' : value.text.replace('.', ',');
  return value.unit === undefined || value.text === '' ? shown : `${shown} ${value.unit}`;
}

/** Agrégats déclarés d'une synthèse (`FormSummary`, `bind.aggregates`) : libellé, valeur exacte, unité. */
export function AggregateList({ values }: { values: readonly AggregateValue[] }) {
  if (values.length === 0) return null;
  return (
    <dl className="ay-summary__aggregates" data-testid="summary-aggregates">
      {values.map((value) => (
        <div key={value.aggregate_id} data-testid={`summary-aggregate-${value.aggregate_id}`}>
          <dt>{text(value.label)}</dt>
          <dd className="ay-ledger__num">{aggregateShown(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Lecture seule (`readonly_when`, ADR-146 §2.9) : le champ reste lisible et focalisable, jamais `disabled` ;
 * la saisie est ignorée par l'appelant. Présentation seulement : le serveur valide comme s'il était modifiable.
 */
export function ReadonlyScope({ readonly, children }: { readonly: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = ref.current;
    if (root === null) return;
    for (const control of root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')) {
      control.readOnly = readonly;
      if (readonly) control.setAttribute('aria-readonly', 'true');
      else control.removeAttribute('aria-readonly');
    }
  });
  if (!readonly) return <>{children}</>;
  return (
    <div ref={ref} className="ay-form__readonly" data-readonly="true">
      {children}
    </div>
  );
}

/** Question avant l'envoi (`layout.confirmation`, §2.8) : titre, message, « Valider » ou « Revenir » (rien ne part). */
export function ConfirmationDialog({ title, message, confirmLabel, cancelLabel, onConfirm, onCancel }: { title: string; message: string | undefined; confirmLabel: string; cancelLabel: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <Dialog
      title={title}
      open
      onClose={onCancel}
      size="sm"
      footer={<DialogActions cancel={{ label: cancelLabel, onClick: onCancel }} primary={{ label: confirmLabel, onClick: onConfirm }} />}
    >
      {message !== undefined && message !== '' && (
        <p className="ay-text" data-testid="confirmation-message">
          {message}
        </p>
      )}
    </Dialog>
  );
}

/** Date civile AAAA-MM-JJ montrée JJ/MM/AAAA, sans conversion de fuseau ; absente ou illisible : « — ». */
export function civilShown(date: string | undefined): string {
  const match = date === undefined ? null : /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return match === null ? '—' : `${match[3]}/${match[2]}/${match[1]}`;
}

/**
 * Bandeau fixe du formulaire (invariant de l'utilisateur du 2026-10-19, révisé le 2026-10-20) : la date de la
 * fiche, l'opérateur et « Sortir », à chaque page et dans les quatre modes ; les blocs de la région d'en-tête
 * (bandeau d'objet) s'y rangent. « Valider » n'y est plus qu'un raccourci discret, avant la dernière page :
 * l'action principale est en bas (FormActionBar).
 */
export function FormBanner({ sheetDate, operator, disabled, onExit, validateShortcut = false, children }: { sheetDate: string | undefined; operator: string | undefined; disabled: boolean; onExit?: (() => void) | undefined; validateShortcut?: boolean; children?: ReactNode }) {
  return (
    <div className="ay-form-banner" role="region" aria-label="Bandeau de la fiche" data-testid="form-banner">
      <div className="ay-form-banner__facts">
        <span className="ay-form-banner__fact">
          <span className="ay-form-banner__label">Fiche du</span>
          <strong data-testid="banner-date">{civilShown(sheetDate)}</strong>
        </span>
        <span className="ay-form-banner__fact">
          <span className="ay-form-banner__label">Opérateur</span>
          <strong data-testid="banner-operator">{operator === undefined || operator === '' ? '—' : operator}</strong>
        </span>
      </div>
      {children !== undefined && <div className="ay-form-banner__blocks">{children}</div>}
      <div className="ay-form-banner__actions">
        {validateShortcut && (
          <button type="submit" className="ay-form-banner__shortcut" disabled={disabled} data-testid="banner-validate">
            Valider maintenant
          </button>
        )}
{onExit !== undefined && (
        <button type="button" className="ay-button ay-button--secondary ay-form-banner__exit" onClick={onExit}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l-5-5 5-5M5 12h11" />
          </svg>
          Sortir
        </button>
        )}
      </div>
    </div>
  );
}

/**
 * Barre d'actions du bas (maquette du 2026-10-20) : une seule action principale. « Suivant » sur une page qui
 * n'est pas la dernière, « Valider la fiche » sur la dernière ou sur une fiche d'une seule page ; « Précédent »
 * à gauche, la position au milieu.
 */
export function FormActionBar({ position, onPrevious, onNext, disabled, center, compact = false }: { position: string; onPrevious?: (() => void) | undefined; onNext?: (() => void) | undefined; disabled: boolean; center?: ReactNode; compact?: boolean }) {
  // Téléphone (ADR-146 addendum (15)) : Précédent en icône, progression courte (« 2/3 »), l'action principale.
  return (
    <div className={`ay-form-actions${compact ? ' ay-form-actions--compact' : ''}`} role="group" aria-label="Navigation de la fiche">
      <div className="ay-form-actions__start">
        {onPrevious !== undefined &&
          (compact ? (
            <button type="button" className="ay-button ay-button--secondary ay-form-actions__button ay-form-actions__icon" onClick={onPrevious} aria-label="Précédent" title="Précédent">
              <span aria-hidden="true">‹</span>
            </button>
          ) : (
            <button type="button" className="ay-button ay-button--secondary ay-form-actions__button" onClick={onPrevious}>
              <span aria-hidden="true">‹ </span>Précédent
            </button>
          ))}
      </div>
      <div className="ay-form-actions__center">
        {center ?? (
          <span className="ay-form-actions__position" data-testid="wizard-progress">
            {position}
          </span>
        )}
      </div>
      <div className="ay-form-actions__end">
        {onNext !== undefined ? (
          <button type="button" className="ay-button ay-form-actions__button ay-form-actions__primary" onClick={onNext}>
            Suivant<span aria-hidden="true"> ›</span>
          </button>
        ) : (
          <button type="submit" className="ay-button ay-form-actions__button ay-form-actions__primary" disabled={disabled} aria-busy={disabled}>
            <span aria-hidden="true">✓ </span>Valider la fiche
          </button>
        )}
      </div>
    </div>
  );
}

/** « Sortir » : enregistrer sur ce navigateur et sortir, sortir sans enregistrer, ou annuler (rien ne change). */
export function ExitDialog({ onSaveAndLeave, onLeave, onCancel }: { onSaveAndLeave: () => void; onLeave: () => void; onCancel: () => void }) {
  return (
    <Dialog
      title="Sortir de la fiche ?"
      open
      onClose={onCancel}
      size="md"
      footer={<DialogActions destructive={{ label: 'Sortir sans enregistrer', onClick: onLeave }} cancel={{ label: 'Annuler', onClick: onCancel }} primary={{ label: 'Enregistrer et sortir', onClick: onSaveAndLeave }} />}
    >
      <p className="ay-text">« Enregistrer et sortir » garde vos réponses sans valider la fiche : la tâche reste en cours et se reprend depuis ce poste ou un autre.</p>
    </Dialog>
  );
}
